import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { useRegisterCadastrosPageActions } from "../layout";
import { STATUS_LABELS } from "../shared/status";

const DashboardCadastros = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { notifyError } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, abertas: 0, por_status: {} });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("Painel/API/Cadastros/Produtos/Dashboard/index.php");
      setData(res.data || { total: 0, abertas: 0, por_status: {} });
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useRegisterCadastrosPageActions({
    onRefresh: fetchData,
    refreshing: loading,
  });

  const kpis = [
    { label: "Total", value: data.total, path: "/modulo/cadastros/produtos" },
    { label: "Em aberto", value: data.abertas, path: "/modulo/cadastros/produtos" },
    {
      label: STATUS_LABELS.AGUARDANDO_ESTOQUE,
      value: data.por_status?.AGUARDANDO_ESTOQUE || 0,
      path: "/modulo/cadastros/produtos/estoque",
    },
    {
      label: STATUS_LABELS.AGUARDANDO_MASTER,
      value: data.por_status?.AGUARDANDO_MASTER || 0,
      path: "/modulo/cadastros/produtos/master",
    },
    {
      label: STATUS_LABELS.EM_CADASTRO,
      value: data.por_status?.EM_CADASTRO || 0,
      path: "/modulo/cadastros/produtos/cadastro",
    },
    {
      label: STATUS_LABELS.CADASTRADO,
      value: data.por_status?.CADASTRADO || 0,
      path: "/modulo/cadastros/produtos",
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, overflow: "auto", height: "100%" }}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Visão geral — cadastro de produtos
      </Typography>

      <Card
        variant="outlined"
        sx={(theme) => ({
          mb: 2.5,
          borderRadius: 2,
          borderColor: alpha(theme.palette.secondary.main, 0.45),
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
        })}
      >
        <CardActionArea
          onClick={() => navigate("/modulo/cadastros/produtos/consulta")}
          sx={{ minHeight: 72 }}
        >
          <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={(theme) => ({
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    color: "secondary.dark",
                    flexShrink: 0,
                  })}
                >
                  <SearchOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    Localizar Produtos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consulta rápida na SB1 — ideal para mobile
                  </Typography>
                </Box>
              </Stack>
              <ArrowForwardOutlinedIcon color="secondary" />
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
        }}
      >
        {kpis.map((card) => (
          <Card
            key={card.label}
            variant="outlined"
            sx={{
              borderRadius: 2,
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                borderColor: "secondary.main",
                boxShadow: (t) => `0 4px 16px ${alpha(t.palette.primary.main, 0.08)}`,
              },
            }}
          >
            <CardActionArea onClick={() => navigate(card.path)} sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {card.label}
                  </Typography>
                  {loading ? (
                    <Skeleton variant="text" width={64} height={40} />
                  ) : (
                    <Typography variant="h4" fontWeight={800} letterSpacing={-0.5}>
                      {card.value}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default DashboardCadastros;
